import {
  type ExtractPropTypes,
  type PropType,
  computed,
  defineComponent,
} from 'vue'
import { routeProps, useExpose, useParent, useRoute } from '@xzx-design/hooks'
import { createNamespace, extend } from '@xzx-design/utils'
import { Button, type ButtonType } from '@xzx-design/components'
import { ACTION_BAR_KEY } from './action-bar'

// Components

const [name, bem] = createNamespace('action-bar-button')

export const actionBarButtonProps = extend({}, routeProps, {
  type: String as PropType<ButtonType>,
  text: String,
  icon: String,
  color: String,
  loading: Boolean,
  disabled: Boolean,
})

export type ActionBarButtonProps = ExtractPropTypes<typeof actionBarButtonProps>

export default defineComponent({
  name,

  props: actionBarButtonProps,

  setup(props, { slots }) {
    const route = useRoute()
    const { parent, index } = useParent(ACTION_BAR_KEY)

    // eslint-disable-next-line vue/return-in-computed-property
    const isFirst = computed(() => {
      if (parent) {
        const prev = parent.children[index.value - 1]
        return !(prev && 'isButton' in prev)
      }
    })

    // eslint-disable-next-line vue/return-in-computed-property
    const isLast = computed(() => {
      if (parent) {
        const next = parent.children[index.value + 1]
        return !(next && 'isButton' in next)
      }
    })

    useExpose({ isButton: true })

    return () => {
      const { type, icon, text, color, loading, disabled } = props

      return (
        <Button
          class={bem([
            type,
            {
              last: isLast.value,
              first: isFirst.value,
            },
          ])}
          size="large"
          type={type}
          icon={icon}
          color={color}
          loading={loading}
          disabled={disabled}
          onClick={route}
        >
          {slots.default ? slots.default() : text}
        </Button>
      )
    }
  },
})
